import { AssistantCompany } from '@mews-commander/data-access';
import { Icon, IconName, Stack } from '@optimus-web/core';
import { LocalizedText, useLocalizeText } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { useResolveRoute } from '@mews/routing';
import { getInlineAddress, useCommanderSelector } from '@mews-commander/utils';
import { getCurrentEnterpriseChainId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isCurrentProfileEnabledToManageCompanies } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { ResultListProps } from '../results/ListTitle';
import { highlight } from '../../utils/highlight';
import { stringifyPrimaryText } from '../../utils/array';
import { navigateInBaseModal, showRouteInModal } from '../../../app/actions/common';
import { StyledBadge, StyledLeftIcon } from '../styles';
import { AssistantSearchType, SearchType } from '../../assistantSearchTypes';
import { AssistantSearchListItemButton } from '../results/searchListItemButton';
import { AssistantFilterChips } from '../../assistantSearchTypeFilters';
import { DescriptionItem, SearchItemDescription } from '../components/SearchItemDescription';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface CompanyListProps extends ResultListProps {
    companies: AssistantCompany[];
    selectedFilter?: AssistantSearchType | AssistantFilterChips;
}

interface PrimaryTextProps {
    company: AssistantCompany;
    searchTerm: string;
}

const PrimaryText = ({ company, searchTerm }: PrimaryTextProps) => {
    const { name, department, isTravelAgency } = company;
    const nameAndDepartment = highlight(stringifyPrimaryText([name, department || '']), searchTerm);
    const travelAgency = useLocalizeText()({ textKey: TextKey.TravelAgency });
    return (
        <Stack vertical={false} spacing="50">
            <span>{nameAndDepartment}</span>
            {isTravelAgency && <StyledBadge content={travelAgency} variant="subtle" />}
        </Stack>
    );
};

interface SecondaryTextProps {
    company: AssistantCompany;
    searchTerm: string;
}
const SecondaryText = ({ company, searchTerm }: SecondaryTextProps) => {
    const { address } = company;
    const inlineAddress =
        address &&
        getInlineAddress({
            addressLine1: address.addressLine1,
            city: address.city,
            countryCode: address.countryCode,
            postalCode: address.postalCode,
        });

    const descriptions: DescriptionItem[] = inlineAddress ? [{ value: highlight(inlineAddress, searchTerm) }] : [];
    return <SearchItemDescription descriptions={descriptions} />;
};

export const useCompanyList = ({
    companies,
    searchTerm,
    selectedFilter,
}: CompanyListProps): AssistantSearchListItemProps[] => {
    const resolveRoute = useResolveRoute();
    const isCompaniesManager = useCommanderSelector<boolean>(isCurrentProfileEnabledToManageCompanies);
    const chainId = useCommanderSelector<string>(getCurrentEnterpriseChainId);
    const list: Array<AssistantSearchListItemProps> = [];

    companies.forEach((company) => {
        const url = resolveRoute(RouteName.CompanyDetail, { id: company.id });
        list.push({
            itemId: company.id,
            primaryText: <PrimaryText company={company} searchTerm={searchTerm} />,
            secondaryText: <SecondaryText company={company} searchTerm={searchTerm} />,
            leftIcon: <StyledLeftIcon size="small" value={IconName.Companies} />,
            routeAction: navigateInBaseModal(url),
            searchType: SearchType.Company,
        });
    });
    if (isCompaniesManager && companies.length > 0 && selectedFilter === SearchType.Company) {
        list.push({
            itemId: AssistantSearchListItemButton.NewCompany,
            primaryText: <LocalizedText textKey={TextKey.PartnerCompanyNew} />,
            leftIcon: <Icon size="small" value={IconName.PlusSimple} />,
            routeAction: showRouteInModal(RouteName.CompanyCreate, {
                query: {
                    ownerId: chainId,
                    name: searchTerm,
                },
            }),
        });
    }
    return list;
};
